import * as React from 'react'
import { faClose, FaIcon } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import ModalLayout from '../../../drivers/modals/shared/ModalLayout'
import useCompanyGroupService from '../../services/CompanyGroupService'
import { toast } from '@/lib/toasts/api'

interface AddCompanyGroupModalProps {
  companyId: string
  close: () => void
  onGroupAdded: () => void
}

const AddCompanyGroupModal: React.FC<AddCompanyGroupModalProps> = ({
  companyId,
  close,
  onGroupAdded,
}) => {
  const { t } = useLocalize()
  const { addGroup } = useCompanyGroupService()
  const [groupName, setGroupName] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!groupName.trim()) {
      toast.error({ text: t('addCompany.groupNameEmpty') })
      return
    }

    setIsSubmitting(true)
    try {
      const result = await addGroup({ companyId, name: groupName.trim() })
      if (result) {
        toast.success({ text: t('addCompany.groupCreated') })
        onGroupAdded()
      } else {
        toast.error({ text: t('addCompany.groupCreateError') })
      }
    } catch (err) {
      toast.error({ text: t('addCompany.groupCreateError') })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalLayout>
      <div className="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-rounded-lg tw-bg-white">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-p-4">
          <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
            {t('addCompany.addNewGroup')}
          </p>
          <FaIcon
            icon={faClose}
            className="tw-h-5 tw-w-5 tw-cursor-pointer"
            onClick={() => close()}
          />
        </div>

        <form onSubmit={handleSubmit} className="tw-p-4">
          <div className="tw-mb-4">
            <label
              htmlFor="groupName"
              className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-content-base"
            >
              {t('addCompany.groupName')}
            </label>
            <input
              id="groupName"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary"
              placeholder={t('addCompany.enterGroupName')}
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          <div className="tw-flex tw-justify-end tw-gap-2">
            <button
              type="button"
              className="tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50"
              onClick={() => close()}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('addCompany.adding') : t('common.add')}
            </button>
          </div>
        </form>
      </div>
    </ModalLayout>
  )
}

export default AddCompanyGroupModal
